import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Chip } from '../components/ui/Chip'
import { EmptyState } from '../components/ui/EmptyState'
import { apiClient } from '../lib/api/client'
import { formatMoney } from '../verticals/cart/cart-money'
import { AddProjectAffordance } from '../verticals/orders/AddProjectAffordance'
import { PartThumbnails } from '../verticals/orders/PartThumbnails'
import { RfqRowMenu } from '../verticals/rfq/RfqRowMenu'
import {
  RFQ_STATUS_CHIP,
  rfqRowTitle,
  shipByForQuote,
  subtotalForQuote,
} from '../verticals/rfq/domain'
import type { Rfq } from '../verticals/rfq/domain'
import { useRfqQuote } from '../verticals/rfq/use-rfq-quote'

export interface OpenQuotesProps {
  /** Only fetch when the session is authenticated — `account:read` is an
   * account-baseline scope, but there's no session to scope it to when
   * signed out, so we skip the call entirely rather than let it 401. */
  readonly authenticated: boolean
}

/** Most-recent rows shown in the Home "Open quotes" tab (ADR-0095 D0).
 *
 * UID-603 — this now drives the REQUEST (`?limit=6`) as well as the render, so
 * the widget fetches exactly what it shows instead of fetching the whole account
 * and slicing. One constant, one meaning. */
const MAX_ROWS = 6

/**
 * The statuses this widget calls "open" (ADR-0095 D0), now sent to the server as
 * a repeated `?status=` filter rather than applied after the fetch.
 *
 * `accepted` is deliberately absent, as it always was — an accepted quote is an
 * order-in-progress, not an open quote. `OpenQuoteRow`'s `showsQuote` predicate
 * relies on that exclusion (see its comment).
 */
const OPEN_QUOTE_STATUSES = ['requested', 'quoting', 'quoted'] as const

/** Accessible name for the open-quotes card list, mirroring
 * `RECENT_ORDERS_LABEL` (`RecentOrders.tsx`) — code review follow-up: six
 * sibling rows with no group name and no "list with N items" announcement is
 * a navigability regression against that shipped precedent. */
const OPEN_QUOTES_LABEL = 'Your open quotes'

/**
 * Live "Open quotes" widget for the Home activity panel (ADR-0095, UID-302).
 * Sibling of `RecentOrders.tsx` — same `authenticated` gate and same
 * idle/loading/loaded/error state shape. Unlike `RecentOrders` this takes NO
 * `onCountChange` prop (ADR-0095 D5): the Home tab badge was already removed
 * in the UID-212 follow-up, and `handleOrdersCountChange` is a documented
 * no-op — propagating that vestigial seam into a second call site is
 * explicitly forbidden by the ADR.
 *
 * UID-603 (ADR-0176 D1.3/D6 #3) — `GET /rfq` now returns the shared
 * `{ items, nextCursor, totalCount, statusCounts }` envelope, and this widget
 * asks it for exactly what it renders: `?status=requested&status=quoting&
 * status=quoted&limit=6`. The status filter and the row bound BOTH moved to the
 * server; the old client-side `.filter().slice()` is gone. It is still
 * newest-first server-side, so there is no client-side re-sort (ADR-0095 D0).
 *
 * Every derivation (`rfqRowTitle`, `subtotalForQuote`, `shipByForQuote`)
 * lives in `verticals/rfq/domain.ts`, never here (ADR-0056 D4) — so `/rfq`
 * and Home can never drift.
 */
export function OpenQuotes({ authenticated }: OpenQuotesProps) {
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle')
  const [rfqs, setRfqs] = useState<readonly Rfq[]>([])
  /**
   * UID-603 (ADR-0176 D6 #3) — the count of OPEN quotes matching the server-side
   * filter, independent of the page. `null` until a load succeeds.
   *
   * The empty state MUST key off this rather than off `rfqs.length`. Before
   * UID-603 this widget fetched every RFQ of the account and then filtered +
   * sliced client-side, so `openQuotes.length === 0` was a true statement about
   * the whole account. Now that the fetch is a bounded page, `rfqs.length === 0`
   * only says "this page is empty" — and rendering "No open quotes yet" off that
   * is a FALSE EMPTY STATE the user cannot distinguish from having no quotes.
   */
  const [totalOpen, setTotalOpen] = useState<number | null>(null)

  useEffect(() => {
    if (!authenticated) {
      setLoadState('idle')
      setRfqs([])
      setTotalOpen(null)
      return
    }

    let cancelled = false
    setLoadState('loading')

    async function load() {
      try {
        // UID-603 (ADR-0176 D6 #3) — ASK THE SERVER FOR EXACTLY WHAT THIS WIDGET
        // RENDERS: the latest `MAX_ROWS` OPEN quotes.
        //
        // It used to fetch the account's ENTIRE RFQ list — every row with its
        // `lines` array and its whole enrichment tail (tax rates, S3 + Catalog
        // thumbnail fan-out) — and then filter-and-slice down to 6 in the
        // browser. Two things were wrong with that, and only one of them is
        // about payload size:
        //   * COST: the widget is on the Home page, so every visit paid for the
        //     whole tenant's RFQ list to render six rows.
        //   * CORRECTNESS: filter-THEN-slice over a bounded page yields a FALSE
        //     EMPTY STATE whenever the page is dominated by `expired`/`declined`/
        //     `draft` rows. Filtering server-side is what makes "the latest 6
        //     open quotes" the thing actually requested, rather than "the latest
        //     6 RFQs of any status, hope enough of them are open".
        //
        // `status` is REPEATABLE, which is why the three-status set is one
        // request rather than three: a single-valued filter could not express it.
        const { data, error } = await apiClient.GET('/rfq', {
          params: {
            // Spread rather than passed by reference: the generated client
            // types the repeated param as a MUTABLE array, and this constant is
            // `as const` so it cannot be handed over directly.
            query: { status: [...OPEN_QUOTE_STATUSES], limit: MAX_ROWS },
          },
        })
        if (cancelled) return
        if (data) {
          setRfqs(data.items)
          // `totalCount` is present because this request carries no `cursor`.
          setTotalOpen(data.totalCount ?? data.items.length)
          setLoadState('loaded')
          return
        }
        if (error && (error as { status?: number }).status === 401) {
          return // Handled globally by authMiddleware's session invalidation.
        }
        setLoadState('error')
      } catch {
        if (cancelled) return
        setLoadState('error')
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [authenticated])

  if (!authenticated || loadState === 'idle') {
    return (
      <EmptyState
        title="No open quotes yet"
        detail="Orders and RFQs you submit will show up here."
      />
    )
  }

  if (loadState === 'loading') {
    return <OpenQuotesSkeleton />
  }

  if (loadState === 'error') {
    return (
      <EmptyState
        title="Couldn't load your open quotes"
        detail="Something went wrong loading your quotes. Try refreshing the page."
      />
    )
  }

  // UID-603 — the server already applied both the status filter and the row
  // bound (see the fetch above), so `rfqs` IS this widget's page. The
  // `.filter().slice()` that used to live here is GONE rather than kept as a
  // belt-and-braces cap: keeping it would leave two places that decide which
  // statuses are "open", and the day they disagree the widget silently renders a
  // different set than the count beside it.
  const openQuotes = rfqs

  // Keyed off the SERVER's filtered total, not off the page length — see
  // `totalOpen`'s doc. `null` means "no successful load yet", which the
  // idle/loading/error branches above have already handled, so treating it as
  // non-empty here cannot produce a spurious empty state.
  if (totalOpen === 0) {
    return (
      <EmptyState
        title="No open quotes yet"
        detail="Orders and RFQs you submit will show up here."
      />
    )
  }

  return (
    <ul className="open-quotes-card" aria-label={OPEN_QUOTES_LABEL}>
      {openQuotes.map((rfq) => (
        <OpenQuoteRow key={rfq.id} rfq={rfq} />
      ))}
    </ul>
  )
}

function OpenQuoteRow({ rfq }: { readonly rfq: Rfq }) {
  const navigate = useNavigate()
  const chip = RFQ_STATUS_CHIP[rfq.status]
  const title = rfqRowTitle(rfq)
  // ADR-0095 D4: gate the per-row quote fetch to quoted-only (after the D0
  // filter, `accepted` never reaches this row, so this is equivalent to
  // `RfqListRow`'s `(status === 'quoted' || status === 'accepted') &&
  // Boolean(rfq.quoteId)` predicate, just narrowed by the filter above).
  const showsQuote = rfq.status === 'quoted' && Boolean(rfq.quoteId)
  const { quote } = useRfqQuote(rfq.id, showsQuote)

  // ADR-0095 D2: `quote?.createdAt ?? rfq.submittedAt` — while the quote
  // fetch is in flight (or for a non-quoted row), `quote` is `undefined`, so
  // this naturally reads `rfq.submittedAt` first and is monotonically
  // refined once the quote loads. NEVER falls back to `createdAt`
  // (ADR-0093 D5) — when both are null, the line is omitted entirely.
  const lastModified = quote?.createdAt ?? rfq.submittedAt

  const shipBy = showsQuote && quote ? shipByForQuote(quote) : null
  const subtotal = showsQuote && quote ? subtotalForQuote(quote) : null

  function handleRowActivate() {
    navigate(`/rfq/${rfq.id}`)
  }

  function handleRowClick(_event: ReactMouseEvent<HTMLDivElement>) {
    handleRowActivate()
  }

  // ADR-0095 D7: reuses UID-230's nested-interactive discipline verbatim —
  // ignores a keydown that bubbled up from a nested control (the "+ Add
  // project" button, the kebab trigger) rather than landing on the row
  // itself, so Enter/Space on a nested control only activates ITS own
  // behavior, never also navigates the row.
  function handleRowKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleRowActivate()
    }
  }

  return (
    // The list item itself keeps its implicit `listitem` role (an explicit
    // role on this element would override it, per the ARIA spec, and break
    // the list's `<ul>`/`<li>` containment) — the row's OWN `role="link"`
    // lives one level down, on the div that carries the click/keydown
    // handlers, exactly as before this was wrapped in a list.
    <li className="open-quotes-row-item">
      <div
        className="open-quotes-row"
        // biome-ignore lint/a11y/useSemanticElements: a real `<a>` cannot be
        // used here — it would have to contain nested interactive descendants
        // (the "+ Add project" button, the kebab trigger), and HTML forbids
        // interactive content inside `<a>`. `role="link"` on this `<div>` is
        // the same axe-verified alternative `RfqListRow.tsx` documents (its
        // ADR-0056 Decision 4 doc comment) — `role="link"` is exempt from
        // axe-core's `nested-interactive` rule, unlike `role="button"`.
        role="link"
        tabIndex={0}
        // Human label only — the aria-label NEVER carries the raw rfq id
        // (ADR-0095 D1's no-raw-id rule).
        aria-label={`View RFQ ${title} — ${chip.label}`}
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
      >
        <div className="open-quotes-cell open-quotes-title-cell">
          <div className="open-quotes-title-row">
            <span className="open-quotes-title">{title}</span>
            {rfq.bundled === true && (
              // ADR-0095 D7: a STATIC, non-interactive span — not the
              // keyboard-operable disclosure `<button>` `RfqCard`/`RfqListRow`
              // use elsewhere. Carries VISIBLE text ("Bundled"), never a
              // color-only signal (WCAG 1.4.1). `RFQ_BUNDLED_NOTE` is
              // deliberately NOT surfaced here (ADR-0074's copy convergence
              // stays unforked).
              <span
                className="rfq-card-bundled-badge rfq-card-bundled-badge-static"
                title="Bundled — purchased as one"
              >
                Bundled
              </span>
            )}
          </div>
          {lastModified && (
            <div className="open-quotes-last-modified">
              Last modified: <time dateTime={lastModified}>{lastModified.slice(0, 10)}</time>
            </div>
          )}
        </div>
        <div className="open-quotes-cell">
          <span className="open-quotes-cell-label">Project</span>
          <AddProjectAffordance orderLabel={title} />
        </div>
        <div className="open-quotes-cell open-quotes-thumbs">
          {/* UID-302 follow-up (human decision, ADR-0095 §"Superseding
          decision"): no `max` override — the `PartThumbnails` DEFAULT of 2
          is what the sibling Recent orders tab renders. `.dc.html` shows 3
          for this row, but intra-page consistency with the shipped Recent
          orders tab was explicitly chosen over literal design fidelity. */}
          <PartThumbnails count={rfq.lines.length} thumbnailUrls={rfq.thumbnailUrls} />
        </div>
        <div className="open-quotes-cell">
          {/* UID-302 follow-up (same decision): the shared `.chip` base, with
          NO `.rfq-chip` modifier. `.rfq-chip` squares the pill to 4px radius
          /weight 500 per `.dc.html`, but the Recent orders tab renders the
          full-pill /weight-700 shared base — its own squaring rule
          (`.recent-orders-table .chip`, orders.css) is a DEAD selector,
          since the widget renders `.recent-orders-card`. Dropping the
          modifier here makes the two tabs' pills identical without editing
          orders.css. Keep `.rfq-chip` on the /rfq surfaces (RfqCard,
          RfqListRow) — this is scoped to the Home row only. */}
          <Chip tone={chip.tone}>{chip.label}</Chip>
        </div>
        <div className="open-quotes-cell">
          <span className="open-quotes-cell-label">Ship by</span>
          <span className="open-quotes-cell-value">{shipBy ?? 'N/A'}</span>
        </div>
        <div className="open-quotes-cell">
          <span className="open-quotes-cell-label">Subtotal</span>
          <span className="open-quotes-cell-value">
            {subtotal ? formatMoney(subtotal.amount, subtotal.currency) : 'N/A'}
          </span>
        </div>
        <div className="open-quotes-cell open-quotes-menu">
          <RfqRowMenu rfqId={rfq.id} rfqLabel={title} />
        </div>
      </div>
    </li>
  )
}

function OpenQuotesSkeleton() {
  return (
    <ul className="open-quotes-card" aria-hidden="true">
      {Array.from({ length: MAX_ROWS }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length, never-reordered skeleton rows
        <li key={index} className="open-quotes-row open-quotes-row-skeleton">
          <div className="skeleton" style={{ width: '55%', height: 16 }} />
        </li>
      ))}
    </ul>
  )
}
